import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { useLiceController_GetTemperature } from '@/services/hooks';
import type { LiceController_GetTemperatureQueryParams, TemperatureResult } from '@/services/types';

type TemperatureOptions = Parameters<typeof useLiceController_GetTemperature>[2];

export type CageTemperatureResult = Omit<UseQueryResult<unknown, unknown>, 'data'> & {
    data: TemperatureResult[];
};

/**
 * Sea temperature for the cage, for the wounds graphs.
 *
 * The lice pages read temperature off their daily rows now, so the wounds graphs are the last
 * reader of this endpoint and the hook lives beside them. The rows are passed on as served:
 * `enrichBodyWoundsDataWithTemperature` normalises the days when it joins them.
 */
export function useGetCageTemperature(
    cageId: number,
    params: LiceController_GetTemperatureQueryParams,
    options?: TemperatureOptions
): CageTemperatureResult {
    const query = useLiceController_GetTemperature(cageId, params, options);
    const rows = query.data?.data;
    // Memoised so the caller's join is not redone every render.
    const data = useMemo(() => rows ?? [], [rows]);

    return { ...query, isLoading: query.isPending, data };
}
